import { useState } from "react";
import { redeemItems, pointHistory } from "../../../mocks/dashboard";
import { useGrowStore } from "../../../hooks/useGrowStore";

// ── Won Burst Animation ────────────────────────────────────────────
function WonBurst({ onDone }: { onDone: () => void }) {
  return (
    <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center">
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            animation: `coinFly${i % 4} 0.9s ease-out forwards`,
            animationDelay: `${i * 0.04}s`,
          }}
        >
          <div
            className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD600] to-[#FFA500] flex items-center justify-center text-xs font-black text-white"
            style={{ boxShadow: "0 2px 8px rgba(255,214,0,0.6)" }}
          >
            ₩
          </div>
        </div>
      ))}
      <style>{`
        @keyframes coinFly0 { 0%{opacity:1;transform:translate(0,0) scale(1)} 100%{opacity:0;transform:translate(80px,-120px) scale(0.4)} }
        @keyframes coinFly1 { 0%{opacity:1;transform:translate(0,0) scale(1)} 100%{opacity:0;transform:translate(-80px,-100px) scale(0.4)} }
        @keyframes coinFly2 { 0%{opacity:1;transform:translate(0,0) scale(1)} 100%{opacity:0;transform:translate(120px,40px) scale(0.4)} }
        @keyframes coinFly3 { 0%{opacity:1;transform:translate(0,0) scale(1)} 100%{opacity:0;transform:translate(-100px,60px) scale(0.4)} }
      `}</style>
    </div>
  );
}

// ── Seed Money Coin Display ─────────────────────────────────────────
function CoinDisplay({ points }: { points: number }) {
  return (
    <div className="relative flex items-center justify-center">
      <div className="absolute bottom-0 w-24 h-4 bg-[#FFD600]/20 rounded-full blur-md" />
      {[3, 2, 1].map((layer) => (
        <div
          key={layer}
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: layer * 4,
            background: layer === 1
              ? "linear-gradient(145deg, #FFE566, #FFD600, #FFA500)"
              : "linear-gradient(145deg, #FFD600, #FFA500)",
            boxShadow: layer === 1
              ? "inset -4px -4px 8px rgba(0,0,0,0.15), inset 3px 3px 6px rgba(255,255,255,0.5), 0 4px 12px rgba(255,214,0,0.4)"
              : "0 2px 4px rgba(0,0,0,0.1)",
            zIndex: layer,
          }}
        >
          {layer === 1 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xl font-black text-white/90 drop-shadow">₩</span>
            </div>
          )}
        </div>
      ))}
      <div style={{ width: 80, height: 80 + 3 * 4 }} />
    </div>
  );
}

// ── Redeem Modal ────────────────────────────────────────────────────
function RedeemModal({
  item,
  userPoints,
  onConfirm,
  onClose,
}: {
  item: (typeof redeemItems)[0];
  userPoints: number;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const canAfford = userPoints >= item.points;
  const isSubscription = item.category === "구독료 방어";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0">
      <div
        className="bg-white rounded-3xl w-full max-w-sm p-8 relative animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center text-[#191F28]/30 hover:text-[#191F28] cursor-pointer"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="text-5xl text-center mb-4">{item.icon}</div>
        <h3 className="text-xl font-black text-[#191F28] text-center mb-1">{item.name}</h3>
        {isSubscription && (
          <p className="text-xs text-center text-[#00D873] font-bold mb-3">구독료 방어 아이템</p>
        )}

        <div className="flex items-center justify-center gap-2 mb-8">
          <span className="text-2xl">₩</span>
          <span className="text-3xl font-black text-[#191F28]">{item.points.toLocaleString()}</span>
          <span className="text-sm text-[#191F28]/40 self-end mb-1">시드머니</span>
        </div>

        <div className="bg-[#F2F4F6] rounded-2xl p-4 mb-3 flex items-center justify-between">
          <span className="text-sm text-[#191F28]/60">현재 잔고</span>
          <span className={`font-black text-lg ${canAfford ? "text-[#00D873]" : "text-red-400"}`}>
            {userPoints.toLocaleString()}원
          </span>
        </div>

        {canAfford ? (
          <div className="bg-[#00D873]/5 border border-[#00D873]/20 rounded-2xl p-3 mb-4">
            <p className="text-xs text-[#00D873] font-bold text-center">
              {isSubscription
                ? `시드머니로 다음 달 AI 인턴 채용 비용을 아꼈어요! 💸`
                : `시드머니 ${item.points.toLocaleString()}원을 사용할게요`}
            </p>
          </div>
        ) : (
          <div className="bg-red-50 rounded-2xl p-3 mb-4">
            <p className="text-xs text-red-400 font-bold text-center">
              잔고 부족 — {(item.points - userPoints).toLocaleString()}원 더 필요해요
            </p>
          </div>
        )}

        <button
          onClick={canAfford ? onConfirm : undefined}
          className={`w-full py-4 rounded-2xl font-black text-base transition-all duration-200 whitespace-nowrap ${
            canAfford
              ? "bg-[#00D873] text-white hover:opacity-90 hover:-translate-y-0.5 cursor-pointer"
              : "bg-[#F2F4F6] text-[#191F28]/30 cursor-not-allowed"
          }`}
        >
          {canAfford ? "시드머니로 결제하기 💰" : "잔고 부족"}
        </button>
      </div>
    </div>
  );
}

// ── Success Toast ───────────────────────────────────────────────────
function SuccessToast({ msg, show }: { msg: string; show: boolean }) {
  return (
    <div
      className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#191F28] text-white px-5 py-3 rounded-2xl text-sm font-bold flex items-center gap-2 transition-all duration-300 ${
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      <i className="ri-check-line text-[#00D873]" />
      {msg}
    </div>
  );
}

// ── Main Panel ──────────────────────────────────────────────────────
export default function GrowPointsPanel() {
  const { store } = useGrowStore();
  const [points, setPoints] = useState(store.totalPoints);
  const [tab, setTab] = useState<"spend" | "history">("spend");
  const [selected, setSelected] = useState<(typeof redeemItems)[0] | null>(null);
  const [showBurst, setShowBurst] = useState(false);
  const [redeemed, setRedeemed] = useState<number[]>([]);
  const [history, setHistory] = useState([...pointHistory]);
  const [toastMsg, setToastMsg] = useState("");
  const [showToast, setShowToast] = useState(false);

  const showSuccessToast = (msg: string) => {
    setToastMsg(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleConfirm = () => {
    if (!selected) return;
    setPoints((p) => p - selected.points);
    setRedeemed((prev) => [...prev, selected.id]);
    setHistory((prev) => [
      {
        id: Date.now(),
        desc: `${selected.name} 결제`,
        points: -selected.points,
        date: "방금",
        type: "spend" as const,
      },
      ...prev,
    ]);
    setShowBurst(true);
    setTimeout(() => setShowBurst(false), 1000);

    const msg = selected.category === "구독료 방어"
      ? `시드머니로 다음 달 AI 인턴 채용 비용을 ${selected.points.toLocaleString()}원 아꼈어요 💸`
      : `${selected.name} 결제 완료! 잔고에서 ${selected.points.toLocaleString()}원이 출금됐어요`;
    showSuccessToast(msg);
    setSelected(null);
  };

  const royaltyTotal = history
    .filter((h) => h.type === "royalty")
    .reduce((sum, h) => sum + h.points, 0);
  const earnTotal = history
    .filter((h) => h.type === "earn")
    .reduce((sum, h) => sum + h.points, 0);

  return (
    <div className="bg-white rounded-3xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#191F28] to-[#2D3748] p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 bg-[#FFD600]/10 rounded-full translate-x-16 -translate-y-16 pointer-events-none" />
        <div className="flex items-center justify-between relative z-10">
          <div>
            <div className="text-white/50 text-xs font-bold tracking-widest uppercase mb-1">내 시드머니 💰</div>
            <div className="flex items-baseline gap-2">
              <span className="text-white text-xl font-bold">₩</span>
              <span className="text-4xl font-black text-white">{points.toLocaleString()}</span>
            </div>
            <div className="text-white/40 text-xs mt-1.5">
              {redeemItems.filter((r) => r.points <= points).length}개 아이템 구매 가능
            </div>
          </div>
          <div className="animate-float">
            <CoinDisplay points={points} />
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 mt-6 relative z-10">
          {[
            { label: "이번 달 배당금", val: `+${earnTotal}원` },
            { label: "프롬프트 로열티", val: `+${royaltyTotal}원` },
            { label: "총 출금", val: `${redeemed.length + 1}회` },
          ].map((s) => (
            <div key={s.label} className="bg-white/10 rounded-xl px-3 py-2.5 text-center">
              <div className="text-white text-sm font-black">{s.val}</div>
              <div className="text-white/40 text-[10px] mt-0.5 leading-tight">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Royalty banner */}
        {royaltyTotal > 0 && (
          <div className="mt-4 relative z-10 bg-[#FFD600]/10 border border-[#FFD600]/20 rounded-xl px-4 py-2.5 flex items-center gap-2">
            <span className="text-base">💰</span>
            <p className="text-xs text-[#FFD600] font-bold">
              자는 동안에도 로열티 {royaltyTotal}원이 들어왔어요!
            </p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#F2F4F6] px-6">
        {(["spend", "history"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`py-4 px-2 mr-6 text-sm font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer ${
              tab === t
                ? "border-[#191F28] text-[#191F28]"
                : "border-transparent text-[#191F28]/40 hover:text-[#191F28]"
            }`}
          >
            {t === "spend" ? "시드머니 쓰기" : "입출금 내역"}
          </button>
        ))}
      </div>

      {/* Spend Grid */}
      {tab === "spend" && (
        <div className="p-6">
          {/* Category sections */}
          {["구독료 방어", "프리미엄 콘텐츠", "카페", "편의점", "쇼핑", "배달", "구독"].map((cat) => {
            const catItems = redeemItems.filter((item) => item.category === cat);
            if (catItems.length === 0) return null;
            return (
              <div key={cat} className="mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-black text-[#191F28]/40 tracking-widest uppercase">{cat}</span>
                  {cat === "구독료 방어" && (
                    <span className="text-[10px] bg-[#00D873]/10 text-[#00D873] font-bold px-2 py-0.5 rounded-full">
                      추천
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {catItems.map((item) => {
                    const done = redeemed.includes(item.id);
                    const affordable = points >= item.points;
                    return (
                      <button
                        key={item.id}
                        onClick={() => !done && setSelected(item)}
                        className={`rounded-2xl p-4 text-left transition-all duration-200 cursor-pointer border-2 ${
                          done
                            ? "border-[#00D873]/30 bg-[#00D873]/5"
                            : affordable
                            ? "border-transparent bg-[#F2F4F6] hover:-translate-y-0.5 hover:border-[#00D873]/30"
                            : "border-transparent bg-[#F2F4F6] opacity-50"
                        }`}
                      >
                        <div className="text-3xl mb-3">{item.icon}</div>
                        <div className="text-xs font-black text-[#191F28] leading-tight mb-2">{item.name}</div>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] font-black text-[#FFD600]">₩</span>
                          <span className={`text-xs font-bold ${affordable && !done ? "text-[#191F28]" : "text-[#191F28]/40"}`}>
                            {done ? "결제 완료 ✅" : `${item.points.toLocaleString()}원`}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* History */}
      {tab === "history" && (
        <div className="p-6 space-y-3">
          {history.map((h) => {
            const isRoyalty = h.type === "royalty";
            const isEarn = h.type === "earn";
            return (
              <div key={h.id} className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm flex-shrink-0 ${
                  isRoyalty ? "bg-[#FFD600]/15" : isEarn ? "bg-[#00D873]/10" : "bg-red-50"
                }`}>
                  {isRoyalty ? "💰" : isEarn ? "📈" : "🛍️"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#191F28] leading-snug">{h.desc}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-[#191F28]/40">{h.date}</span>
                    {isRoyalty && (
                      <span className="text-[10px] bg-[#FFD600]/10 text-[#FFD600] font-bold px-1.5 py-0.5 rounded-full">
                        패시브 인컴
                      </span>
                    )}
                    {isEarn && (
                      <span className="text-[10px] bg-[#00D873]/10 text-[#00D873] font-bold px-1.5 py-0.5 rounded-full">
                        배당금
                      </span>
                    )}
                  </div>
                </div>
                <div className={`font-black text-sm flex-shrink-0 ${
                  isRoyalty ? "text-[#FFD600]" : isEarn ? "text-[#00D873]" : "text-red-400"
                }`}>
                  {h.points > 0 ? "+" : ""}{h.points.toLocaleString()}원
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showBurst && <WonBurst onDone={() => setShowBurst(false)} />}
      <SuccessToast msg={toastMsg} show={showToast} />
      {selected && (
        <RedeemModal
          item={selected}
          userPoints={points}
          onConfirm={handleConfirm}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
